import React, { useEffect, useMemo } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { SearchableSelect } from '@/components/common/SearchableSelect';
import {
  buildVendorTaxIdRows,
  buildVendorTaxIdsFromRows,
  isMaskedSensitiveValue,
  VENDOR_TAX_ID_OPTIONS,
} from '@/lib/vendorTaxIds';
import type { CreateVendorRequest, VendorSearchResponse } from '@/types/api';
import { useGetErpConnections } from '@/api/hooks/useErpConnection';

interface VendorFormProps {
  initialData?: VendorSearchResponse;
  onSubmit: (data: CreateVendorRequest) => Promise<void>;
  isLoading?: boolean;
  onCancel: () => void;
}

const optionalText = z.string().trim().optional();
const optionalEmail = z
  .string()
  .trim()
  .refine((value) => !value || z.string().email().safeParse(value).success, 'Email must be a valid email')
  .optional();
const optionalUrl = z
  .string()
  .trim()
  .refine((value) => !value || z.string().url().safeParse(value).success, 'Website must be a valid URL')
  .optional();

const VENDOR_STATUS_OPTIONS = [
  { id: 'active', label: 'Active' },
  { id: 'inactive', label: 'Inactive' },
  { id: 'blocked', label: 'Blocked' },
];

const vendorSchema = z.object({
  erp_connection_id: optionalText,
  legal_name: z.string().trim().min(1, 'Legal name is required'),
  company_name: optionalText,
  print_on_check_name: optionalText,
  status: optionalText,
  default_payment_terms: optionalText,
  currency_preference: optionalText,
  is_1099: z.boolean().optional(),
  email: optionalEmail,
  phone: optionalText,
  mobile: optionalText,
  alternate_phone: optionalText,
  fax: optionalText,
  website: optionalUrl,
  address: optionalText,
  bank_account: optionalText,
  realm_id: optionalText,
  qb_vendor_id: optionalText,
  qb_account_num: optionalText,
  qb_ap_account_ref: optionalText,
  qb_sync_token: optionalText,
  risk_flags_text: optionalText,
  tax_id_rows: z.array(
    z.object({
      type: optionalText,
      value: optionalText,
    })
  ),
  bill_addr_line1: optionalText,
  bill_addr_line2: optionalText,
  bill_addr_line3: optionalText,
  bill_addr_city: optionalText,
  bill_addr_state: optionalText,
  bill_addr_postal_code: optionalText,
  bill_addr_country: optionalText,
}).superRefine((data, ctx) => {
  data.tax_id_rows.forEach((row, index) => {
    const hasType = Boolean(row.type?.trim());
    const hasValue = Boolean(row.value?.trim());
    if (hasType !== hasValue) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['tax_id_rows', index, hasType ? 'value' : 'type'],
        message: 'Provide both a tax ID type and value',
      });
    }
  });
});

type VendorFormData = z.infer<typeof vendorSchema>;

const defaultFormValues: VendorFormData = {
  erp_connection_id: '',
  legal_name: '',
  company_name: '',
  print_on_check_name: '',
  status: 'active',
  default_payment_terms: 'Net 30',
  currency_preference: '',
  is_1099: false,
  email: '',
  phone: '',
  mobile: '',
  alternate_phone: '',
  fax: '',
  website: '',
  address: '',
  bank_account: '',
  realm_id: '',
  qb_vendor_id: '',
  qb_account_num: '',
  qb_ap_account_ref: '',
  qb_sync_token: '',
  risk_flags_text: '',
  tax_id_rows: [{ type: 'CA_QST', value: '' }],
  bill_addr_line1: '',
  bill_addr_line2: '',
  bill_addr_line3: '',
  bill_addr_city: '',
  bill_addr_state: '',
  bill_addr_postal_code: '',
  bill_addr_country: '',
};

function getStringValue(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function getFormValues(initialData?: VendorSearchResponse): VendorFormData {
  if (!initialData) {
    return defaultFormValues;
  }

  const billAddr = initialData.bill_addr || {};

  return {
    erp_connection_id: '',
    legal_name: initialData.legal_name || '',
    company_name: initialData.company_name || '',
    print_on_check_name: initialData.print_on_check_name || '',
    status: initialData.status || 'active',
    default_payment_terms: initialData.default_payment_terms || 'Net 30',
    currency_preference: initialData.currency_preference || '',
    is_1099: Boolean(initialData.is_1099),
    email: initialData.email || '',
    phone: initialData.phone || '',
    mobile: initialData.mobile || '',
    alternate_phone: initialData.alternate_phone || '',
    fax: initialData.fax || '',
    website: initialData.website || '',
    address: initialData.address || '',
    bank_account: initialData.bank_account || '',
    realm_id: initialData.realm_id || '',
    qb_vendor_id: initialData.qb_vendor_id || '',
    qb_account_num: initialData.qb_account_num || '',
    qb_ap_account_ref: initialData.qb_ap_account_ref || '',
    qb_sync_token: initialData.qb_sync_token || '',
    risk_flags_text: (initialData.risk_flags || []).join('\n'),
    tax_id_rows: buildVendorTaxIdRows(initialData.tax_ids),
    bill_addr_line1: getStringValue(billAddr.line1),
    bill_addr_line2: getStringValue(billAddr.line2),
    bill_addr_line3: getStringValue(billAddr.line3),
    bill_addr_city: getStringValue(billAddr.city),
    bill_addr_state: getStringValue(billAddr.state),
    bill_addr_postal_code: getStringValue(billAddr.postal_code),
    bill_addr_country: getStringValue(billAddr.country),
  };
}

function addIfDefined(
  target: Record<string, unknown>,
  key: string,
  value?: string
) {
  if (value !== undefined) {
    target[key] = value;
  }
}

function normalizeOptionalString(value?: string): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function buildBillAddress(data: VendorFormData): Record<string, string> | undefined {
  const billAddr: Record<string, string> = {};

  const fieldMap: Array<
    [
      | 'bill_addr_line1'
      | 'bill_addr_line2'
      | 'bill_addr_line3'
      | 'bill_addr_city'
      | 'bill_addr_state'
      | 'bill_addr_postal_code'
      | 'bill_addr_country',
      string,
    ]
  > = [
    ['bill_addr_line1', 'line1'],
    ['bill_addr_line2', 'line2'],
    ['bill_addr_line3', 'line3'],
    ['bill_addr_city', 'city'],
    ['bill_addr_state', 'state'],
    ['bill_addr_postal_code', 'postal_code'],
    ['bill_addr_country', 'country'],
  ];

  fieldMap.forEach(([formKey, outputKey]) => {
    const value = normalizeOptionalString(data[formKey]);
    if (value) {
      billAddr[outputKey] = value;
    }
  });

  return Object.keys(billAddr).length > 0 ? billAddr : undefined;
}

function parseRiskFlags(value?: string): string[] | undefined {
  const items = (value || '')
    .split('\n')
    .flatMap((line) => line.split(','))
    .map((item) => item.trim())
    .filter(Boolean);

  return items.length > 0 ? items : undefined;
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-default dark:border-default-dark bg-surface dark:bg-surface-dark p-6">
      <div className="mb-5">
        <h2 className="text-lg font-semibold text-content-primary dark:text-content-primary-dark">{title}</h2>
        <p className="mt-1 text-sm text-content-secondary dark:text-content-secondary-dark">{description}</p>
      </div>
      <div className="grid gap-4 md:grid-cols-2">{children}</div>
    </section>
  );
}

export const VendorForm: React.FC<VendorFormProps> = ({
  initialData,
  onSubmit,
  isLoading = false,
  onCancel,
}) => {
  const initialValues = useMemo(() => getFormValues(initialData), [initialData]);
  const {
    control,
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<VendorFormData>({
    resolver: zodResolver(vendorSchema),
    defaultValues: initialValues,
  });
  const { data: erpConnections = [] } = useGetErpConnections(!initialData);
  const activeErpConnections = useMemo(
    () => erpConnections.filter((connection) => connection.status === 'active'),
    [erpConnections],
  );
  const { fields: taxIdFields, append: appendTaxIdRow, remove: removeTaxIdRow } = useFieldArray({
    control,
    name: 'tax_id_rows',
  });

  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  const handleFormSubmit = async (data: VendorFormData) => {
    if (!initialData && activeErpConnections.length > 0 && !data.erp_connection_id) {
      setError('erp_connection_id', { message: 'Choose the ERP company for this vendor' });
      return;
    }
    const request: CreateVendorRequest = {
      erp_connection_id: data.erp_connection_id || undefined,
      legal_name: data.legal_name.trim(),
      is_1099: Boolean(data.is_1099),
      tax_ids: buildVendorTaxIdsFromRows(data.tax_id_rows),
    };

    [
      'company_name',
      'print_on_check_name',
      'status',
      'default_payment_terms',
      'currency_preference',
      'email',
      'phone',
      'mobile',
      'alternate_phone',
      'fax',
      'website',
      'address',
      'bank_account',
      'realm_id',
      'qb_vendor_id',
      'qb_account_num',
      'qb_ap_account_ref',
      'qb_sync_token',
    ].forEach((field) => {
      const rawValue = data[field as keyof VendorFormData] as string | undefined;
      if (field === 'bank_account' && isMaskedSensitiveValue(rawValue)) {
        return;
      }
      addIfDefined(
        request as unknown as Record<string, unknown>,
        field,
        normalizeOptionalString(rawValue)
      );
    });

    request.bill_addr = buildBillAddress(data);
    request.risk_flags = parseRiskFlags(data.risk_flags_text);

    await onSubmit(request);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      <Section
        title="Vendor Basics"
        description="Capture the names, status, and accounting defaults used across AP workflows."
      >
        {!initialData && activeErpConnections.length > 0 && (
          <div className="md:col-span-2">
            <label className="mb-1.5 block text-sm font-medium text-content-secondary">ERP company</label>
            <Controller
              name="erp_connection_id"
              control={control}
              render={({ field }) => (
                <SearchableSelect
                  value={field.value || ''}
                  onChange={field.onChange}
                  disabled={isLoading}
                  options={[
                    { id: '', label: 'Choose ERP company' },
                    ...activeErpConnections.map((connection) => ({
                      id: connection.id,
                      label: connection.display_name || connection.external_company_name || `${connection.erp_type} ${connection.realm_id}`,
                    })),
                  ]}
                  placeholder="Choose ERP company"
                  clearable={false}
                />
              )}
            />
            {errors.erp_connection_id?.message && <p className="mt-1.5 text-xs text-red-500">{errors.erp_connection_id.message}</p>}
          </div>
        )}
        <Input
          id="legal_name"
          label="Vendor Name"
          {...register('legal_name')}
          disabled={isLoading}
          error={errors.legal_name?.message}
        />
        <Input
          id="company_name"
          label="Company Name"
          {...register('company_name')}
          disabled={isLoading}
          error={errors.company_name?.message}
        />
        <Input
          id="print_on_check_name"
          label="Print On Check Name"
          {...register('print_on_check_name')}
          disabled={isLoading}
          error={errors.print_on_check_name?.message}
        />
        <div>
          <label className="mb-1.5 block text-sm font-medium text-content-secondary">Status</label>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <SearchableSelect
                value={field.value || 'active'}
                onChange={field.onChange}
                disabled={isLoading}
                options={VENDOR_STATUS_OPTIONS}
                placeholder="Status"
                clearable={false}
              />
            )}
          />
          {errors.status?.message && <p className="mt-1.5 text-xs text-red-500">{errors.status.message}</p>}
        </div>
        <Input
          id="default_payment_terms"
          label="Default Payment Terms"
          {...register('default_payment_terms')}
          disabled={isLoading}
          error={errors.default_payment_terms?.message}
        />
        <Input
          id="currency_preference"
          label="Currency Preference"
          {...register('currency_preference')}
          disabled={isLoading}
          error={errors.currency_preference?.message}
        />
        <div className="md:col-span-2">
          <label className="flex items-center gap-3 rounded-lg border border-default dark:border-default-dark px-4 py-3 text-sm text-content-primary dark:text-content-primary-dark">
            <input type="checkbox" {...register('is_1099')} disabled={isLoading} className="h-4 w-4 rounded border-default" />
            1099 eligible vendor
          </label>
        </div>
      </Section>

      <Section
        title="Contact Details"
        description="Store the vendor contacts your team uses for invoices, remittance, and support."
      >
        <Input id="email" label="Email" type="email" {...register('email')} disabled={isLoading} error={errors.email?.message} />
        <Input id="website" label="Website" {...register('website')} disabled={isLoading} error={errors.website?.message} />
        <Input id="phone" label="Phone" {...register('phone')} disabled={isLoading} error={errors.phone?.message} />
        <Input id="mobile" label="Mobile" {...register('mobile')} disabled={isLoading} error={errors.mobile?.message} />
        <Input
          id="alternate_phone"
          label="Alternate Phone"
          {...register('alternate_phone')}
          disabled={isLoading}
          error={errors.alternate_phone?.message}
        />
        <Input id="fax" label="Fax" {...register('fax')} disabled={isLoading} error={errors.fax?.message} />
        <div className="md:col-span-2">
          <label className="mb-1.5 block text-sm font-medium text-content-secondary">Address</label>
          <textarea
            {...register('address')}
            disabled={isLoading}
            rows={3}
            className="input min-h-[96px]"
            placeholder="Mailing or remittance address"
          />
          {errors.address?.message && <p className="mt-1.5 text-xs text-red-500">{errors.address.message}</p>}
        </div>
      </Section>

      <Section
        title="Billing Address"
        description="Capture a structured billing address for downstream ERP or bill sync workflows."
      >
        <Input id="bill_addr_line1" label="Line 1" {...register('bill_addr_line1')} disabled={isLoading} error={errors.bill_addr_line1?.message} />
        <Input id="bill_addr_line2" label="Line 2" {...register('bill_addr_line2')} disabled={isLoading} error={errors.bill_addr_line2?.message} />
        <Input id="bill_addr_line3" label="Line 3" {...register('bill_addr_line3')} disabled={isLoading} error={errors.bill_addr_line3?.message} />
        <Input id="bill_addr_city" label="City" {...register('bill_addr_city')} disabled={isLoading} error={errors.bill_addr_city?.message} />
        <Input id="bill_addr_state" label="State / Region" {...register('bill_addr_state')} disabled={isLoading} error={errors.bill_addr_state?.message} />
        <Input
          id="bill_addr_postal_code"
          label="Postal Code"
          {...register('bill_addr_postal_code')}
          disabled={isLoading}
          error={errors.bill_addr_postal_code?.message}
        />
        <Input id="bill_addr_country" label="Country" {...register('bill_addr_country')} disabled={isLoading} error={errors.bill_addr_country?.message} />
      </Section>

      <Section
        title="Tax, Risk, And Payment"
        description="Track the tax ID, remittance account, and internal risk indicators associated with the vendor."
      >
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <label className="block text-sm font-medium text-content-secondary">Tax IDs</label>
            <Button
              type="button"
              variant="secondary"
              onClick={() => appendTaxIdRow({ type: 'CA_QST', value: '' })}
              disabled={isLoading}
            >
              Add Tax ID
            </Button>
          </div>
          <div className="space-y-2">
            {taxIdFields.map((field, index) => (
              <div key={field.id} className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,180px)_minmax(0,1fr)_auto]">
                <Controller
                  name={`tax_id_rows.${index}.type`}
                  control={control}
                  render={({ field: taxIdField }) => (
                    <SearchableSelect
                      value={taxIdField.value || ''}
                      onChange={taxIdField.onChange}
                      disabled={isLoading}
                      aria-label={`Tax ID type ${index + 1}`}
                      options={[
                        { id: '', label: 'Select tax ID type' },
                        ...VENDOR_TAX_ID_OPTIONS.map((option) => ({ id: option.value, label: option.label })),
                      ]}
                      placeholder="Select tax ID type"
                      clearable={false}
                    />
                  )}
                />
                <input
                  id={`tax_id_value_${index}`}
                  type="text"
                  {...register(`tax_id_rows.${index}.value`)}
                  disabled={isLoading}
                  className="input"
                  placeholder="Enter tax ID number"
                  aria-label={`Tax ID value ${index + 1}`}
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => removeTaxIdRow(index)}
                  disabled={isLoading || taxIdFields.length === 1}
                >
                  Remove
                </Button>
                {(errors.tax_id_rows?.[index]?.type?.message || errors.tax_id_rows?.[index]?.value?.message) && (
                  <p className="sm:col-span-2 mt-1.5 text-xs text-red-500">
                    {errors.tax_id_rows?.[index]?.type?.message || errors.tax_id_rows?.[index]?.value?.message}
                  </p>
                )}
              </div>
            ))}
            {errors.tax_id_rows?.root?.message && (
              <p className="mt-1.5 text-xs text-red-500">{errors.tax_id_rows.root.message}</p>
            )}
          </div>
        </div>
        <Input
          id="bank_account"
          label="Bank Account"
          {...register('bank_account')}
          disabled={isLoading}
          error={errors.bank_account?.message}
        />
        {initialData?.bank_account && (
          <p className="-mt-2 text-xs text-content-secondary md:col-span-2">
            Leave the masked value unchanged to keep the stored account, or enter a replacement.
          </p>
        )}
        <div className="md:col-span-2">
          <label className="mb-1.5 block text-sm font-medium text-content-secondary">Risk Flags</label>
          <textarea
            {...register('risk_flags_text')}
            disabled={isLoading}
            rows={4}
            className="input min-h-[112px]"
            placeholder="One flag per line, or comma-separated"
          />
          {errors.risk_flags_text?.message && <p className="mt-1.5 text-xs text-red-500">{errors.risk_flags_text.message}</p>}
        </div>
      </Section>

      <div className="flex gap-3 pt-2">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={isLoading} loading={isLoading}>
          {initialData ? 'Update Vendor' : 'Create Vendor'}
        </Button>
      </div>
    </form>
  );
};
